/**
 * The orchestrator client. Reuses the shared apiClient (and its Entra token
 * acquisition) but points at the orchestrator origin instead of platform-api.
 * The origin itself lives in `lib/agentApi.ts` — shared with the guardrails
 * formatter, which is also an orchestrator route.
 */
import { apiRequest, getAuthToken, ApiError } from '../../lib/apiClient.js';
import { agentBaseUrl } from '../../lib/agentApi.js';
import type {
  AgentRequest,
  AgentThreadListResponse,
  AgentThreadMessagesResponse,
  BotThread,
  ConversationFeedbackResponse,
} from '../../lib/types.js';

export interface AgentFeedbackRequest {
  conversationId: string;
  /** The rated assistant message's id — from the stream's `done` frame. */
  messageId: string;
  rating: 'up' | 'down';
  comment?: string;
  categories?: string[];
}

/**
 * Record a 👍/👎 on an assistant turn. Unlike the chat stream, feedback is
 * plain REST over the AgentFeedback table, so it lives on the platform API
 * (default base — no `baseUrl` override), NOT the orchestrator. Keyed by the
 * assistant message's id (the `done` frame's `messageId`), so it correlates the
 * same whether left live or after a reload.
 */
export const submitAgentFeedback = (req: AgentFeedbackRequest): Promise<void> =>
  apiRequest<void>('/api/feedback', {
    method: 'POST',
    body: req,
  });

/**
 * The caller's own ratings for a conversation (`GET /api/feedback/mine`), so the
 * chat can rehydrate which turns are already rated after a reload. Platform API,
 * not the orchestrator. Keyed by messageId.
 */
export const getConversationFeedback = (
  conversationId: string,
): Promise<ConversationFeedbackResponse> =>
  apiRequest<ConversationFeedbackResponse>(
    `/api/feedback/mine?conversationId=${encodeURIComponent(conversationId)}`,
  );

export interface AgentStreamHandlers {
  /** The conversation id (first `meta` frame) — lets a new thread settle its id early. */
  onMeta?: (meta: { conversationId: string }) => void;
  /** A chunk of the assistant's answer. */
  onDelta: (text: string) => void;
  /**
   * A tool started running — a friendly "working…" label (e.g. "Checking live
   * telemetry") for an ephemeral status chip. Fires before/between text while
   * the agent uses a tool; the next `onDelta` means text is taking over.
   */
  onStatus?: (label: string) => void;
  /** A tool call started — begin (or extend) the turn's activity record. */
  onToolStart?: (start: {
    id: string;
    label: string;
    doneLabel: string;
    detail: string | null;
  }) => void;
  /** That call finished — flip its row to done/failed with its timing. */
  onToolEnd?: (end: { id: string; ok: boolean; durationMs: number | null }) => void;
  /**
   * The turn finished cleanly. `messageId` is the assistant message's id — the
   * feedback correlation for this turn (undefined only if it couldn't be read back).
   */
  onDone?: (info: { conversationId: string; messageId?: string }) => void;
  /** A pre-stream failure (e.g. 403) or a mid-stream error. */
  onError: (err: unknown) => void;
}

/** Parse one SSE frame ("event: x\ndata: {...}") into its event name + payload. */
const parseSseFrame = (frame: string): { event?: string; data?: Record<string, unknown> } => {
  let event: string | undefined;
  let dataRaw = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataRaw += line.slice('data:'.length).trim();
  }
  if (!dataRaw) return { event };
  try {
    return { event, data: JSON.parse(dataRaw) as Record<string, unknown> };
  } catch {
    return { event };
  }
};

/**
 * Streaming send — the orchestrator returns Server-Sent Events
 * (`meta` → `delta`… → `done` | `error`), surfaced as they arrive. Uses a raw
 * `fetch` (not `apiRequest`) so we can read the body incrementally. A non-OK
 * response (the gate's 403, etc.) is parsed and reported via `onError` with an
 * `ApiError`, so the caller maps it the same as a buffered failure.
 */
export const streamAgentMessage = async (
  req: AgentRequest,
  handlers: AgentStreamHandlers,
): Promise<void> => {
  let res: Response;
  try {
    const token = await getAuthToken();
    res = await fetch(`${agentBaseUrl}/api/agent`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(req),
    });
  } catch (err) {
    handlers.onError(err);
    return;
  }

  if (!res.ok || !res.body) {
    let body: { error?: string; message?: string } | null = null;
    try {
      body = (await res.json()) as { error?: string; message?: string };
    } catch {
      /* non-JSON error body — fall back to status text */
    }
    handlers.onError(
      new ApiError(res.status, body?.message ?? body?.error ?? res.statusText, body?.error),
    );
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let conversationId = req.conversationId ?? '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const { event, data } = parseSseFrame(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        if (event === 'meta') {
          conversationId = (data?.['conversationId'] as string | undefined) ?? conversationId;
          handlers.onMeta?.({ conversationId });
        } else if (event === 'delta') {
          const text = data?.['text'] as string | undefined;
          if (text) handlers.onDelta(text);
        } else if (event === 'status') {
          const label = data?.['label'] as string | undefined;
          if (label) handlers.onStatus?.(label);
        } else if (event === 'tool-start') {
          const id = data?.['id'] as string | undefined;
          const label = data?.['label'] as string | undefined;
          if (id && label) {
            handlers.onToolStart?.({
              id,
              label,
              doneLabel: (data?.['doneLabel'] as string | undefined) ?? label,
              detail: (data?.['detail'] as string | null | undefined) ?? null,
            });
          }
        } else if (event === 'tool-end') {
          const id = data?.['id'] as string | undefined;
          if (id) {
            handlers.onToolEnd?.({
              id,
              ok: data?.['ok'] !== false,
              durationMs: typeof data?.['durationMs'] === 'number' ? data['durationMs'] : null,
            });
          }
        } else if (event === 'done') {
          handlers.onDone?.({
            conversationId,
            messageId: data?.['messageId'] as string | undefined,
          });
        } else if (event === 'error') {
          const code = String(data?.['error'] ?? 'stream_failed');
          handlers.onError(new ApiError(502, code, code));
        }
      }
    }
  } catch (err) {
    handlers.onError(err);
  }
};

/** The caller's past conversations (server-side; tenant + user scoped). */
export const listAgentThreads = (): Promise<AgentThreadListResponse> =>
  apiRequest<AgentThreadListResponse>('/api/agent/threads', { baseUrl: agentBaseUrl });

/** The caller's existing conversation for a case (null = none yet) — so the
 * alert-card deep link reuses instead of minting a duplicate per click. */
export const getCaseThread = (ref: string): Promise<{ conversationId: string | null }> =>
  apiRequest<{ conversationId: string | null }>(
    `/api/agent/case-thread?ref=${encodeURIComponent(ref)}`,
    { baseUrl: agentBaseUrl },
  );

/** The caller's existing bot threads, one per bot. Creates none. */
export const listBotThreads = (): Promise<{ threads: BotThread[] }> =>
  apiRequest<{ threads: BotThread[] }>('/api/agent/bot-threads', { baseUrl: agentBaseUrl });

/** The caller's thread with one of their bots, created the first time. */
export const openBotThread = (botId: string): Promise<{ conversationId: string }> =>
  apiRequest<{ conversationId: string }>(`/api/agent/bots/${encodeURIComponent(botId)}/thread`, {
    method: 'POST',
    baseUrl: agentBaseUrl,
  });

/** Permanently delete a conversation. */
export const deleteAgentThread = (threadId: string): Promise<void> =>
  apiRequest<void>(`/api/agent/threads/${encodeURIComponent(threadId)}`, {
    method: 'DELETE',
    baseUrl: agentBaseUrl,
  });

/** Replay one conversation's messages. */
/** One page of a thread: the newest messages, or those before the `before` message. */
export const getAgentThreadMessages = (
  threadId: string,
  before: string | null = null,
): Promise<AgentThreadMessagesResponse> =>
  apiRequest<AgentThreadMessagesResponse>(
    `/api/agent/threads/${encodeURIComponent(threadId)}/messages${
      before ? `?before=${encodeURIComponent(before)}` : ''
    }`,
    { baseUrl: agentBaseUrl },
  );
